import React, { useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { ref, onValue } from 'firebase/database';
import { database } from './firebaseConfig';
import { getStoredChatId } from './chatId';
import { MAIN_CARD_ID, CURRENCY_SYMBOLS } from './cardsStore';
import { sendTransfer } from './transferStore';
import { loadRecipientCards } from './cardLookup';
import './AmountScreen.css';

const CLOSE_MS = 320; // синхронізовано з анімацією закриття в AmountScreen.css
const OPS = ['×', '÷', '+', '−'];

// «На … картку» — знахідний відмінок типу картки отримувача. capital —
// у плашці вибору картки monobank пише з великої («На Чорну картку»)
const CARD_ACC = {
  black: 'чорну картку', white: 'білу картку', kids: 'дитячу картку', diia: 'Дія.Картку',
  evidnovlennia: 'картку єВідновлення', cashback: 'картку Національний кешбек',
  usd: 'доларову картку', eur: 'єврову картку',
};
function toCardLabel(type, capital) {
  const acc = CARD_ACC[type] || 'картку';
  return `На ${capital ? acc[0].toUpperCase() + acc.slice(1) : acc}`;
}

// Мініатюри карток у шторці вибору (вирізані зі скріншота monobank)
const CARD_THUMBS = {
  black: '/transfer/thumb-black.png',
  white: '/transfer/thumb-white.png',
  diia: '/transfer/thumb-diia.png',
  cashback: '/card-icons/cashback.png',
};
const SHEET_CLOSE_MS = 250; // синхронізовано з анімацією шторки в AmountScreen.css

// BIN-и ПриватБанку — для значка банку на чужій картці
const PRIVAT_BINS = [
  '414949', '414962', '414963', '516874', '516875', '516933', '517691', '521152', '535145',
  '557721', '440588', '462705', '473118', '473114', '473117', '473121', '476339', '545708',
  '432334', '432335', '434156', '440129', '440509', '458120', '462708', '479087',
];

const maskCard = (n) => `${n.slice(0, 4)} ${n.slice(4, 6)}** **** ${n.slice(12)}`;
export const toNum = (s) => parseFloat(String(s).replace(',', '.')) || 0;

// Вираз з калькулятора: числа з точкою, × ÷ + −, «%» після числа — відсоток
// від лівої частини (100+10% = 110) або просто n/100.
export function evaluate(expr) {
  const tokens = expr.match(/\d+(?:\.\d*)?|[×÷+−%]/g) || [];
  const terms = []; // сума доданків, кожен — добуток
  let sign = 1;
  let product = null;
  let mulOp = null;
  const sumSoFar = () => terms.reduce((a, b) => a + b, 0);
  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (/\d/.test(t)) {
      let n = toNum(t);
      if (tokens[i + 1] === '%') {
        n = product === null && terms.length ? (Math.abs(sumSoFar()) * n) / 100 : n / 100;
        i += 1;
      }
      if (product === null) product = n;
      else product = mulOp === '×' ? product * n : (n ? product / n : product);
    } else if (t === '×' || t === '÷') {
      mulOp = t;
    } else if (t === '+' || t === '−') {
      if (product !== null) terms.push(sign * product);
      product = null;
      sign = t === '+' ? 1 : -1;
    }
  }
  if (product !== null) terms.push(sign * product);
  return Math.round(sumSoFar() * 100) / 100;
}

export const formatResult = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/0$/, ''));

// Ввід з клавіатури: лише цифри й одна точка в числі (копійки через точку,
// кому з клавіатури iOS теж перетворюємо на точку), до 2 знаків після неї
export function sanitize(raw) {
  let out = '';
  for (const ch of raw.replace(/,/g, '.')) {
    const last = out.split(/[×÷+−%]/).pop();
    if (/\d/.test(ch)) {
      if (last === '0') out = out.slice(0, -1); // 05 → 5
      else if (/\.\d\d$/.test(last)) continue;
      out += ch;
    } else if (ch === '.') {
      if (last.includes('.') || /%$/.test(out)) continue;
      out += last === '' ? '0.' : '.';
    } else if ([...OPS, '%'].includes(ch)) {
      out += ch;
    }
  }
  return out;
}

// Цифрова клавіатура — своя, як системна в iOS (з рядком калькулятора):
// у вебі під системною iOS завжди малює панель «⌃ ⌄ Done» і зсуває сторінку
const KEYS = [
  ['1', ''], ['2', 'А Б В Г'], ['3', 'Д Е Ж З'],
  ['4', 'И Й К Л'], ['5', 'М Н О П'], ['6', 'Р С Т У'],
  ['7', 'Ф Х Ц Ч'], ['8', 'Ш Щ Ъ Ы'], ['9', 'Ь Э Ю Я'],
];

export function Keypad({ onKey, onOp }) {
  const noFocus = (e) => e.preventDefault();
  return (
    <div className="ams-keypad" onPointerDown={noFocus}>
      <div className="ams-calc">
        {['×', '÷', '+', '−', '%', '='].map((op) => (
          <button type="button" key={op} onClick={() => onOp(op)}>{op}</button>
        ))}
      </div>
      <div className="ams-keys">
        {KEYS.map(([d, letters]) => (
          <button type="button" className="ams-key" key={d} onClick={() => onKey(d)}>
            <span className="ams-key-digit">{d}</span>
            {letters && <span className="ams-key-letters">{letters}</span>}
          </button>
        ))}
        <button type="button" className="ams-key ams-key--flat" onClick={() => onKey('.')}>
          <span className="ams-key-digit ams-key-digit--center">.</span>
        </button>
        <button type="button" className="ams-key" onClick={() => onKey('0')}>
          <span className="ams-key-digit ams-key-digit--center">0</span>
        </button>
        <button type="button" className="ams-key ams-key--flat" onClick={() => onKey('back')} aria-label="Стерти">
          <svg viewBox="0 0 23 19" className="ams-key-back">
            <path d="M7.2 1h12.3A2.5 2.5 0 0 1 22 3.5v12a2.5 2.5 0 0 1-2.5 2.5H7.2a1.5 1.5 0 0 1-1.1-.5L1.4 10.5a1.5 1.5 0 0 1 0-2l4.7-7A1.5 1.5 0 0 1 7.2 1z" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinejoin="round" />
            <path d="M10.5 6l6 7M16.5 6l-6 7" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  );
}

export function CardMini({ type, currency }) {
  return <span className={`ams-mini ams-mini--${type || 'black'}`}>{CURRENCY_SYMBOLS[currency] || '₴'}</span>;
}

// Екран суми переказу. recipient — користувач застосунку (findUserByCard)
// або null — тоді це картка іншого банку (cardNumber). Сума вводиться своєю
// клавіатурою (Keypad), коментар — системною.
export default function AmountScreen({
  recipient, cardNumber, cards, fromCardId, onClose, onSent,
}) {
  const chatId = getStoredChatId();
  const [closing, setClosing] = useState(false);
  const [expr, setExpr] = useState('');
  const [comment, setComment] = useState('');
  const [focused, setFocused] = useState('amount'); // 'amount' — видно Keypad
  const commentRef = useRef(null);
  const rootRef = useRef(null);
  // Поле коментаря на мить «піднімаємо» нагору екрана й фокусуємо там: тоді
  // iOS не прокручує сторінку, щоб показати його над клавіатурою. Коли
  // клавіатура відкрилась (екран уже стиснуто до неї) — повертаємо на місце.
  const [commentLift, setCommentLift] = useState(false);
  const [mainBalance, setMainBalance] = useState(0);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Картка отримувача: на валютну гривні не зараховуємо — тоді основна.
  // Список його гривневих карток довантажуємо; якщо їх кілька — під ім'ям
  // плашка «На … картку ⌄», що відкриває шторку вибору.
  const initialTo = recipient && (recipient.currency || 'UAH') === 'UAH'
    ? { id: recipient.cardId, type: recipient.cardType }
    : { id: MAIN_CARD_ID, type: 'black' };
  const [toCards, setToCards] = useState([initialTo]);
  const [toCardId, setToCardId] = useState(initialTo.id);
  const [toSheet, setToSheet] = useState(null); // null | 'open' | 'closing'
  const toCard = toCards.find((c) => c.id === toCardId) || initialTo;

  useEffect(() => {
    if (!recipient) return undefined;
    let cancelled = false;
    loadRecipientCards(recipient.chatId).then((list) => {
      if (cancelled || !list.length) return;
      setToCards(list);
      setToCardId((id) => (list.some((c) => c.id === id) ? id : list[0].id));
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [recipient]);

  function openToSheet() {
    if (commentRef.current) commentRef.current.blur();
    setToSheet('open');
  }

  function closeToSheet(cardId) {
    if (cardId) setToCardId(cardId);
    setToSheet('closing');
    timerRef.current = setTimeout(() => setToSheet(null), SHEET_CLOSE_MS);
  }
  const [sending, setSending] = useState(false);
  const timerRef = useRef(null);

  // Гривневі картки, з яких можна платити
  const sources = useMemo(
    () => cards.filter((c) => c.id === MAIN_CARD_ID || (c.currency || 'UAH') === 'UAH'),
    [cards],
  );
  const [sourceId, setSourceId] = useState(
    () => (sources.some((c) => c.id === fromCardId) ? fromCardId : MAIN_CARD_ID),
  );
  const source = sources.find((c) => c.id === sourceId) || { id: MAIN_CARD_ID, type: 'black' };
  const balanceOf = (c) => (c.id === MAIN_CARD_ID ? mainBalance : toNum(c.balance));

  useEffect(() => {
    if (!chatId) return undefined;
    return onValue(ref(database, `balance/${chatId}`), (snap) => setMainBalance(toNum(snap.val()?.value)));
  }, [chatId]);

  // Екран — рівно по видимій області: у standalone-PWA на iOS клавіатура
  // перекриває екран, не зменшуючи його, тож низ підтягуємо до клавіатури
  const [viewport, setViewport] = useState(null);
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => setViewport(
      window.innerHeight - vv.height > 80 ? { top: vv.offsetTop, height: vv.height } : null,
    );
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, []);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  useEffect(() => {
    if (!commentLift) return undefined;
    if (viewport) { setCommentLift(false); return undefined; }
    const t = setTimeout(() => setCommentLift(false), 700); // клавіатура так і не з'явилась
    return () => clearTimeout(t);
  }, [commentLift, viewport]);

  // Сторінку під пальцем не тягаємо (інакше iOS прокручує весь документ)
  useEffect(() => {
    const el = rootRef.current;
    const stop = (e) => { if (e.cancelable) e.preventDefault(); };
    el.addEventListener('touchmove', stop, { passive: false });
    return () => el.removeEventListener('touchmove', stop);
  }, []);

  // Увесь рядок (іконка, поле, ghost-копія) — одна ціль: повторні швидкі
  // тапи, поки поле вже у фокусі, нічого не перемикають
  function focusComment(e) {
    if (e.target.closest('.ams-gift')) return;
    if (document.activeElement === commentRef.current) return;
    e.preventDefault();
    flushSync(() => setCommentLift(true));
    commentRef.current.focus({ preventScroll: true });
  }

  function showKeypad() {
    if (commentRef.current) commentRef.current.blur();
    setFocused('amount');
  }

  function pressKey(k) {
    if (k === 'back') setExpr((e) => e.slice(0, -1));
    else setExpr((e) => sanitize(e + k));
  }
  function close() {
    if (closing) return;
    setClosing(true);
    timerRef.current = setTimeout(onClose, CLOSE_MS);
  }

  const amount = evaluate(expr);
  const enough = amount <= balanceOf(source);
  const canSend = amount > 0 && enough && !sending;

  function pressOp(op) {
    if (op === '=') {
      if (expr) setExpr(formatResult(amount));
      return;
    }
    setExpr((e) => {
      if (!e) return e;
      if (op === '%') return /\d$/.test(e) ? `${e}%` : e;
      return /[×÷+−]$/.test(e) ? e.slice(0, -1) + op : e + op;
    });
  }

  async function send() {
    if (!canSend) return;
    setSending(true);
    try {
      const result = await sendTransfer({
        chatId,
        fromCardId: sourceId,
        amount,
        cardNumber,
        recipient: recipient && { ...recipient, cardId: toCard.id, cardType: toCard.type, currency: 'UAH' },
        comment: comment.trim(),
      });
      if (document.activeElement) document.activeElement.blur();
      onSent(result);
    } catch (e) {
      setSending(false);
      window.alert(e.message === 'insufficient_funds'
        ? 'Недостатньо коштів на картці'
        : 'Не вдалося надіслати переказ. Спробуйте ще раз');
    }
  }

  const keepFocus = (e) => e.preventDefault();
  const keyboardOpen = !!viewport && focused === 'comment';
  const bankBadge = !recipient && PRIVAT_BINS.includes(cardNumber.slice(0, 6));
  const shownExpr = expr ? expr.replace(/([×÷+−])/g, ' $1 ') : '0';

  return (
    <div
      ref={rootRef}
      className={`ams-root ${closing ? 'closing' : ''}`}
      style={keyboardOpen ? { top: viewport.top, height: viewport.height, bottom: 'auto' } : undefined}
    >
      {/* низ без safe-area: його займає Keypad або системна клавіатура */}
      <div className="ams-screen kb">
        <button type="button" className="ams-back" onClick={close} aria-label="Назад">
          <svg viewBox="0 0 20 16">
            <path d="M19 8H1.5M8 1.2L1.2 8L8 14.8" fill="none" stroke="#e0e0e0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>

        <div className="ams-recipient">
          <div className="ams-avatar">
            {recipient && recipient.photo ? (
              <img className="ams-avatar-img" src={recipient.photo} alt="" />
            ) : (
              <div className="ams-avatar-img ams-avatar-img--empty">
                <svg viewBox="0 0 24 24">
                  <circle cx="12" cy="8" r="4.2" fill="#fff" />
                  <path d="M4 21c0-4.4 3.6-7.2 8-7.2s8 2.8 8 7.2z" fill="#fff" />
                </svg>
              </div>
            )}
            {recipient && <span className="ams-badge">m</span>}
            {bankBadge && (
              <span className="ams-badge ams-badge--bank">
                <img src="/icons-bot/private24-logo.png" alt="" />
              </span>
            )}
          </div>
          {recipient ? (
            <div className="ams-recipient-text">
              <p className="ams-name">{recipient.name}</p>
              {toCards.length > 1 ? (
                <button type="button" className="ams-to" onClick={openToSheet}>
                  <CardMini type={toCard.type} currency="UAH" />
                  <span>{toCardLabel(toCard.type, true)}</span>
                  <svg viewBox="0 0 14 9" className="ams-chevron">
                    <path d="M1.5 1.5L7 7l5.5-5.5" fill="none" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              ) : (
                <p className="ams-sub">
                  <CardMini type={toCard.type} currency="UAH" />
                  {toCardLabel(toCard.type)}
                </p>
              )}
            </div>
          ) : (
            <p className="ams-name ams-name--card">{maskCard(cardNumber)}</p>
          )}
        </div>

        <div className="ams-middle" onClick={showKeypad}>
          <div className="ams-source-wrap">
            <button
              type="button"
              className="ams-source"
              onPointerDown={keepFocus}
              onClick={(e) => { e.stopPropagation(); setPickerOpen((v) => !v); }}
            >
              <CardMini type={source.type} currency="UAH" />
              <span>{balanceOf(source).toFixed(2)} ₴</span>
              <svg viewBox="0 0 14 9" className="ams-chevron">
                <path d="M1.5 1.5L7 7l5.5-5.5" fill="none" stroke="#e0e0e0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {pickerOpen && (
              <div className="ams-sources">
                {sources.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    className={`ams-source-item ${c.id === sourceId ? 'active' : ''}`}
                    onPointerDown={keepFocus}
                    onClick={(e) => { e.stopPropagation(); setSourceId(c.id); setPickerOpen(false); }}
                  >
                    <CardMini type={c.type} currency="UAH" />
                    <span>{balanceOf(c).toFixed(2)} ₴</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className={`ams-amount ${expr && !enough ? 'over' : ''}`}>{shownExpr} ₴</p>
        </div>

        <div className="ams-bottom">
          {recipient && (
            <>
              <div className="ams-divider" />
              <div className="ams-comment-row" onPointerDown={focusComment}>
                <svg viewBox="0 0 24 24" className="ams-comment-icon">
                  <path d="M12 3.5c-5 0-9 3.3-9 7.6 0 2.2 1.1 4.2 2.9 5.6L5 20.5l4.3-2.1c.9.2 1.8.3 2.7.3 5 0 9-3.3 9-7.6S17 3.5 12 3.5z" fill="#e0e0e0" />
                </svg>
                {commentLift && (
                  <span className={`ams-comment ams-comment--ghost ${comment ? 'filled' : ''}`}>{comment || 'Коментар...'}</span>
                )}
                <input
                  ref={commentRef}
                  className={`ams-comment ${commentLift ? 'ams-comment--lift' : ''}`}
                  type="text"
                  placeholder="Коментар..."
                  value={comment}
                  maxLength={140}
                  onChange={(e) => setComment(e.target.value)}
                  onFocus={() => setFocused('comment')}
                  onBlur={() => setFocused('amount')}
                />
                <button type="button" className="ams-gift" aria-label="Подарунок" onPointerDown={keepFocus}>
                  <svg viewBox="0 0 24 24">
                    <path d="M4 10h16v3H4zM5 13h6v8H6.5A1.5 1.5 0 0 1 5 19.5zM13 13h6v6.5a1.5 1.5 0 0 1-1.5 1.5H13zM11 10h2v11h-2z" fill="#e0e0e0" />
                    <path d="M12 9.5C10.5 6 7 5.5 7 7.8 7 9.5 10 9.6 12 9.5zM12 9.5c1.5-3.5 5-4 5-1.7 0 1.7-3 1.8-5 1.7z" fill="#e0e0e0" />
                  </svg>
                </button>
              </div>
            </>
          )}
          <div className="ams-actions">
            <button type="button" className="ams-schedule" aria-label="Регулярний платіж" onPointerDown={keepFocus}>
              <svg viewBox="0 0 24 24">
                <rect x="2.5" y="4.5" width="17" height="15" rx="3" fill="#8a8a8a" />
                <path d="M6.5 2.5v4M15.5 2.5v4" stroke="#8a8a8a" strokeWidth="2" strokeLinecap="round" />
                <rect x="2.5" y="8.5" width="17" height="1.6" fill="#272727" />
                <circle cx="17.5" cy="17.5" r="5" fill="#272727" />
                <path d="M20 17.5a2.5 2.5 0 1 1-.9-1.9M19.3 14.6v1.6h-1.6" fill="none" stroke="#8a8a8a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <button
              type="button"
              className="ams-send"
              disabled={!canSend}
              onPointerDown={keepFocus}
              onClick={send}
            >
              Надіслати
            </button>
          </div>
          {/* Keypad тримає місце, доки системна клавіатура не відкрилась
              (і з'являється, щойно вона сховалась) — низ не стрибає */}
          {!keyboardOpen && <Keypad onKey={pressKey} onOp={pressOp} />}
        </div>
      </div>

      {toSheet && (
        <div className={`ams-sheet-root ${toSheet === 'closing' ? 'closing' : ''}`}>
          <div className="ams-sheet-backdrop" onClick={() => closeToSheet()} />
          <div className="ams-sheet">
            <div className="ams-sheet-head">
              <div className="ams-sheet-avatar">
                {recipient.photo
                  ? <img className="ams-avatar-img" src={recipient.photo} alt="" />
                  : <div className="ams-avatar-img ams-avatar-img--empty" />}
                <span className="ams-badge">m</span>
              </div>
              <p className="ams-sheet-name">{recipient.name}</p>
            </div>
            <div className="ams-sheet-cards">
              {toCards.map((c) => (
                <button type="button" className="ams-sheet-card" key={c.id} onClick={() => closeToSheet(c.id)}>
                  {CARD_THUMBS[c.type]
                    ? <img className="ams-thumb" src={CARD_THUMBS[c.type]} alt="" />
                    : <span className={`ams-thumb ams-thumb--${c.type}`} />}
                  <span className="ams-sheet-card-text">
                    <span className="ams-sheet-card-name">{c.name}</span>
                    <span className="ams-sheet-card-num">{c.number ? maskCard(c.number) : ''}</span>
                  </span>
                  {c.id === toCard.id && (
                    <svg viewBox="0 0 24 24" className="ams-check">
                      <circle cx="12" cy="12" r="12" fill="#34c759" />
                      <path d="M7 12.4l3.3 3.3L17 9" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
            <div className="ams-sheet-note">
              <img src="/transfer/currency-cards.png" alt="" />
              <p>Щоб переказати валюту, встановіть <b>родинний зв’язок</b> із цією людиною</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
